<script setup>
/**
 * 任务列表：当前公会的全部任务，表格（点表头排序）+ 搜索 + 筛选；点一行，右侧显示详情（TaskDetailPanel），
 * 有权限的人可以在右侧直接新建、修改、删除（撤销）任务。排序、搜索、筛选、分页都在服务端（GET /work/table）。
 */
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Badge, Button, Input, Pagination, Select, Table } from '@drama/ui'
import TaskDetailPanel from '../components/TaskDetailPanel.vue'
import { api, currentGuild, inGuild, session } from '../api.js'

const PAGE = 50
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const query = reactive({ q: '', status: '', holder: '', source: '', sort: 'updated_at', dir: 'desc', offset: 0 })
const rows = ref([])
const total = ref(0)
const loading = ref(false)
const error = ref('')
const members = ref([])
const creating = ref(false)
const workPath = (suffix) => inGuild(`/api/voko/work${suffix}`)
const selectedId = computed(() => (typeof route.query.task === 'string' ? route.query.task : null))
const me = computed(() => (session.user === null ? null : session.user.id))
const person = (id) => (id === null ? t('okr.unassigned') : (members.value.find((item) => item.user_id === id) ?? { handle: id }).handle)
const date = (value) => (value === null ? '' : new Date(value).toLocaleDateString(locale.value))
// 同一个公会里还有旧市场任务（只读），它们多出这几个状态。
const marketOnly = ['pending_approval', 'in_contest', 'reviewing', 'disputed', 'warranty', 'refunded']
const statuses = ['open', 'claimed', 'submitted', 'rework', 'settled', 'cancelled', ...marketOnly]
const statusLabel = (value) => (marketOnly.includes(value) ? t(`board.status.${value}`) : t(`okr.status.${value}`))
const columns = computed(() => [
  { key: 'node_id', label: t('okr.table.node'), width: '84px', mono: true, sortable: true },
  { key: 'title', label: t('okr.taskTitle'), width: '220px', sortable: true },
  { key: 'status', label: t('okr.table.status'), width: '130px', sortable: true },
  { key: 'holder_user_id', label: t('okr.owner'), width: '130px' },
  { key: 'percent', label: t('okr.table.progress'), width: '84px', align: 'right', sortable: true },
  { key: 'due_on', label: t('okr.table.due'), width: '96px', sortable: true },
  { key: 'reward', label: t('okr.reward'), width: '72px', align: 'right', sortable: true },
  { key: 'updated_at', label: t('okr.table.updated'), width: '96px', sortable: true },
])
const statusOptions = computed(() => [{ value: '', label: t('okr.allStatuses') }, ...statuses.map((value) => ({ value, label: statusLabel(value) }))])
const holderOptions = computed(() => [{ value: '', label: t('okr.table.ownerAll') }, { value: 'me', label: t('okr.table.ownerMe') }, { value: 'unassigned', label: t('okr.unassigned') }])
const sourceOptions = computed(() => [{ value: '', label: t('okr.table.sourceAll') }, { value: 'tree', label: t('okr.table.sourceTree') }, { value: 'other', label: t('okr.table.sourceOther') }])

let ticket = 0
async function loadRows() {
  if (currentGuild.slug === null || session.user === null) return
  const mine_ticket = (ticket += 1)
  loading.value = true
  try {
    const params = new URLSearchParams({ q: query.q, status: query.status, holder: query.holder, source: query.source, sort: query.sort, dir: query.dir, limit: String(PAGE), offset: String(query.offset) })
    const result = await api('GET', `${inGuild('/api/voko/work/table')}&${params}`)
    if (mine_ticket !== ticket) return
    rows.value = result.tasks
    total.value = result.total
  } catch (err) { error.value = err instanceof Error ? err.message : String(err) } finally { if (mine_ticket === ticket) loading.value = false }
}
async function loadMembers() {
  if (currentGuild.slug === null || session.user === null) return
  members.value = (await api('GET', workPath('/members'))).members
}

let timer = null
watch(() => [query.q], () => { clearTimeout(timer); timer = setTimeout(() => { query.offset = 0; loadRows() }, 300) })
watch(() => [query.status, query.holder, query.source, query.sort, query.dir], () => { query.offset = 0; loadRows() })
watch(() => query.offset, loadRows)
// 新手引导的「发第一个任务」带 ?new=1 过来：直接打开新建表单（关闭或选中任务时 query 会被清掉）。
watch([() => currentGuild.slug, () => me.value], () => { query.offset = 0; creating.value = route.query.new === '1' && currentGuild.slug !== null; loadMembers().then(loadRows) }, { immediate: true })
onBeforeUnmount(() => clearTimeout(timer))

function sortBy(key) {
  if (query.sort === key) query.dir = query.dir === 'asc' ? 'desc' : 'asc'
  else Object.assign(query, { sort: key, dir: key === 'title' || key === 'node_id' ? 'asc' : 'desc' })
}
// 旧市场任务只在列表里展示，详情还在 Agent 的任务页。
function select(row) { if (row.market === true) return; creating.value = false; router.replace({ path: route.path, query: { task: row.id } }) }
function close() { creating.value = false; router.replace({ path: route.path, query: {} }) }
async function created(id) {
  creating.value = false
  await router.replace({ path: route.path, query: { task: id } })
  await loadRows()
}
const pageSummary = computed(() => t('okr.table.total', { n: total.value, from: total.value === 0 ? 0 : query.offset + 1, to: Math.min(query.offset + PAGE, total.value) }))
</script>

<template>
  <section class="tl" :aria-busy="loading">
    <header class="tl-head">
      <div><h1>{{ t('okr.table.title') }}</h1><p>{{ t('okr.table.subtitle') }}</p></div>
      <Button v-if="currentGuild.slug !== null" @click="creating = true">{{ t('okr.newTask') }}</Button>
    </header>
    <p v-if="currentGuild.slug === null">{{ t('okr.chooseGuild') }}</p>
    <template v-else>
      <div class="tl-toolbar">
        <Input v-model="query.q" type="search" :placeholder="t('okr.table.search')" :aria-label="t('okr.table.search')" />
        <Select v-model="query.status" :options="statusOptions" :aria-label="t('okr.table.status')" size="sm" />
        <Select v-model="query.holder" :options="holderOptions" :aria-label="t('okr.owner')" size="sm" />
        <Select v-model="query.source" :options="sourceOptions" :aria-label="t('okr.table.source')" size="sm" />
      </div>
      <p v-if="error" role="alert">{{ error }}</p>
      <div class="tl-grid" :class="{ 'has-panel': selectedId !== null || creating }">
        <div class="tl-list">
          <Table :columns="columns" :rows="rows" clickable :sort-key="query.sort" :sort-order="query.dir" @sort="sortBy" @row-click="select">
            <template #status="{ row }"><Badge size="sm">{{ statusLabel(row.status) }}</Badge></template>
            <template #holder_user_id="{ row }">{{ row.market === true ? '' : person(row.holder_user_id) }}</template>
            <template #percent="{ row }">{{ row.market === true ? '' : `${row.percent}%` }}</template>
            <template #due_on="{ row }">{{ date(row.due_on) }}</template>
            <template #updated_at="{ row }">{{ date(row.updated_at) }}</template>
          </Table>
          <p v-if="!loading && rows.length === 0">{{ query.q !== '' || query.status !== '' || query.holder !== '' || query.source !== '' ? t('list.noMatch') : t('okr.empty') }}</p>
          <Pagination :has-prev="query.offset > 0" :has-next="query.offset + PAGE < total" :loading="loading" :summary="pageSummary" :prev-label="t('okr.table.prev')" :next-label="t('okr.table.next')" @prev="query.offset = Math.max(0, query.offset - PAGE)" @next="query.offset += PAGE" />
        </div>
        <TaskDetailPanel v-if="selectedId !== null || creating" :task-id="selectedId" :creating="creating" @close="close" @changed="loadRows" @created="created" />
      </div>
    </template>
  </section>
</template>

<style scoped>
.tl { display: grid; gap: var(--drama-space-lg); min-width: 0; }
.tl-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--drama-space-md); }
.tl-head p { color: var(--drama-color-text-muted); }
.tl-toolbar { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); }
.tl-grid { display: grid; gap: var(--drama-space-lg); grid-template-columns: minmax(0, 1fr); align-items: start; }
.tl-grid.has-panel { grid-template-columns: minmax(0, 1fr) minmax(300px, 400px); }
.tl-list { min-width: 0; display: grid; gap: var(--drama-space-md); }
@media (max-width: 1100px) {
  .tl-grid.has-panel { grid-template-columns: minmax(0, 1fr); }
}
</style>
